/**
 * Ledger tag fields the GL mappings visibility rules look at.
 */
export interface LedgerTagVisibility {
  tagType: string;
  feeDefinition?: {
    id: number;
    feeName: string;
    feeClass: string;
  };
}

interface ProductCustomFee {
  id?: number;
}

/**
 * Tag types hidden on the GL mappings tab of a loan product whose
 * [allowHandover] flag is off.
 *
 * The tag row itself is never deleted (see FeeDefinitionWriteService.disable on the
 * backend for the same convention): hiding it client side keeps the GL account the
 * bank already mapped, so re-enabling hand over restores the mapping.
 */
const HAND_OVER_TAG_TYPES: string[] = ['HAND_OVER_SOURCE_ACCOUNT'];

const NO_HIDDEN_TAG_TYPES: string[] = [];

/**
 * Decides whether a product ledger tag should be listed on the GL mappings tab.
 *
 * A hidden tag is also absent from the batch update the tab sends on save, so its
 * stored mapping is left untouched - exactly how orphaned custom fee tags behave.
 *
 * Pure on purpose: it is re-evaluated on every digest, so it must not log.
 */
export const isTagVisible = (tag: LedgerTagVisibility,
                             customFees: ProductCustomFee[] | undefined,
                             hiddenTagTypes: string[] | undefined): boolean => {
  if (hiddenTagTypes && hiddenTagTypes.includes(tag.tagType)) {
    return false;
  }

  if (tag.feeDefinition && tag.feeDefinition.feeClass === 'CUSTOM') {
    return (customFees ?? []).some(fee => fee.id === tag.feeDefinition?.id);
  }

  return true;
};

/**
 * The subset of a product's ledger tags that the GL mappings tab shows - and, because
 * the tab saves exactly what it shows, also the subset the batch update covers.
 */
export const visibleTags = <T extends LedgerTagVisibility>(productTags: T[] | undefined,
                                                           customFees: ProductCustomFee[] | undefined,
                                                           hiddenTagTypes: string[] | undefined): T[] =>
  (productTags ?? []).filter(tag => isTagVisible(tag, customFees, hiddenTagTypes));

/**
 * GL tag types the loan product screen hides for the given hand over setting.
 *
 * The returned arrays are shared constants on purpose: the value is bound one way
 * into <gl-mappings>, and a fresh array on every digest would keep the binding
 * watcher dirty forever.
 */
export const glTagTypesHiddenForLoanProduct = (allowHandover: boolean | undefined): string[] =>
  allowHandover ? NO_HIDDEN_TAG_TYPES : HAND_OVER_TAG_TYPES;
